import {
  createEffect,
  createSignal,
  JSXElement,
  Show,
  untrack
} from 'solid-js';
import { Localized } from '~/i18n';
import { getTranslationKey, listenToRhEvents, rhUtil } from './utils.ts';
import styles from './need-help.module.scss';
import { useAppData } from '~/components/AppData';
import { useLocation } from '@solidjs/router';
import {
  HubspotFormTypes,
  submitHubspotForm
} from '~/utils/productLogging/hubspot.ts';
import logger from '~/utils/logger';
import { HeadphonesIcon } from 'lucide-solid';
import Menu from '~/components/ui/Menu';
import InputField from '~/components/ui/Forms/InputField.tsx';
import { getLocalizedString } from '~/i18n/utils.ts';
import { Button } from '~/components/ui/Button';

const NEED_HELP_FORM_ID = 'need-help-form';

export default function NeedHelpWithAuth(): JSXElement {
  let formRef: HTMLFormElement | undefined;
  let buttonRef: HTMLButtonElement | undefined;
  const appData = useAppData();
  const location = useLocation();

  const [additionalContext, setAdditionalContext] = createSignal(``);

  const resetRevenueHero = () => {
    rhUtil.cleanUp();
    initRevenueHero();
  };

  const sendDataToHubspot = () => {
    try {
      submitHubspotForm(
        {
          email: appData.member?.email ?? '',
          firstName: appData.member?.firstName ?? '',
          lastName: appData.member?.lastName ?? '',
          workspaceDisplayName: appData.workspace?.displayName ?? '',
          additionalQuery: additionalContext()
        },
        HubspotFormTypes.NEED_HELP
      );
    } catch (e) {
      logger.error(`Failed to submit data to hubspot`);
    }
  };

  const initRevenueHero = () => {
    rhUtil.init({
      formSelector: `#${NEED_HELP_FORM_ID}`,
      onLoad: () => {
        listenToRhEvents({
          onMeetingBooked: () => {
            sendDataToHubspot();
            resetRevenueHero();
            setAdditionalContext('');
            setTimeout(() => {
              buttonRef?.click();
            });
          },
          onDialogClosed: () => {
            resetRevenueHero();
          }
        });
      }
    });
  };

  createEffect(() => {
    if (location.pathname && formRef) {
      untrack(() => {
        resetRevenueHero();
      });
    }
  });

  return (
    <Show when={appData.member}>
      <div class={styles.needHelpDialog}>
        <form
          id={NEED_HELP_FORM_ID}
          // action intentionally omitted; handled via onSubmit preventDefault
          action={'javascript:void(0);'}
          method={'post'}
          ref={formRef}
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <input
            type={`hidden`}
            name={`email`}
            value={appData.member!.email}
          />
          <input
            type={`hidden`}
            name={`firstname`}
            value={appData.member!.firstName}
          />
          <input
            type={`hidden`}
            name={`workspace_displayname`}
            value={appData.workspace!.displayName}
          />
          <input
            type={`hidden`}
            name={`query`}
            value={additionalContext()}
          />
          <Menu
            id={`need-help-menu`}
            groups={[
              {
                label: (
                  <div class={`flex flex-col font-normal`}>
                    <h4 class={`text-fg-muted`}>
                      <Localized
                        translationKey={getTranslationKey(
                          'additional_input_header'
                        )}
                      />
                    </h4>
                    <div class={`mt-4`}>
                      <InputField
                        type={`textarea`}
                        label={getLocalizedString(
                          getTranslationKey(`input_label_how_can_we_help`)
                        )}
                        placeholder={getLocalizedString(
                          getTranslationKey(`input_placeholder_how_can_we_help`)
                        )}
                        value={additionalContext()}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                        }}
                        onInput={(e) => {
                          setAdditionalContext(e.currentTarget.value);
                        }}
                        rows={3}
                      />
                    </div>
                    <div class={`mt-2`}>
                      <Button
                        type={`submit`}
                        size={`sm`}
                        disabled={!additionalContext()}
                      >
                        <Localized
                          translationKey={getTranslationKey(
                            `how_can_we_help_cta`
                          )}
                        />
                      </Button>
                    </div>
                  </div>
                )
              }
            ]}
            containerClass={'p-4'}
          >
            <button
              class={`mr-4 px-3 py-[5px] text-sm font-normal hover:bg-bg-muted rounded-md transition-colors delay-75 border border-border-subtle`}
              type={`button`}
              data-testid={`talk-to-an-engineer-header-nav`}
              ref={buttonRef}
            >
              <div class={`flex items-center`}>
                <HeadphonesIcon
                  width={14}
                  class={`mr-1`}
                  strokeWidth={2}
                />
                <Localized translationKey={`cta_labels.talk_to_an_engineer`} />
              </div>
            </button>
          </Menu>
        </form>
      </div>
    </Show>
  );
}
